import React, { useEffect, useState, useRef } from "react";
import { convertBytesToKB } from '../../utils';

// component
import DescriptionInsertMedia from "../Media/DescriptionInsertMedia";
import Dropzone from './Dropzone';
import MediaCard from './MediaCard';
import StorageUsageBar from './StorageUsageBar';

// mui
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

// type
type UploadedFileType = {
    file: File,
    id: number | undefined,
    url: string | null,
    uploadedDate: string,
    uploading: boolean,
}

// type
type UploadProps = {
    redrawByDelete: number | undefined,
    storageUsed: number,
    storageTotal: number,
    setRedrawByUpload: React.Dispatch<React.SetStateAction<Date | undefined>>,
    handleDeleteMedia: (mediaId: number) => void,
}

export default function Upload(props: UploadProps) {
    const { redrawByDelete, storageUsed, storageTotal, setRedrawByUpload, handleDeleteMedia } = props;
    const [errorMessage, setErrorMessage] = useState('');
    const [uploadedFiles, setUploadedFiles] = useState<UploadedFileType[]>([]);
    const [scrollFlag, setScrollFlag] = useState(false); // MediaCardへのスクロール実行フラグ
    const [scrollIndex, setScrollIndex] = useState(0); // MediaCardへのスクロール位置
    const scrollToMediaCardRef = useRef<HTMLDivElement>(null); // MediaCardへのスクロール用ref

    // ファイルが追加されると呼び出される関数
    const handleFileAddition = (file: File, uploadedDate: string) => {
        // 新しい構造体を作成
        const newUploadedFile = { file, id: undefined, url: null, uploadedDate, uploading: true };
        // 古い構造体の配列に新しい構造体を追加して更新
        setUploadedFiles((prevUploadedFiles) => [...prevUploadedFiles, newUploadedFile]);
        // 本関数におけるsetUploadedFiles更新後にスクロール処理を行うためにフラグをセット
        setScrollFlag(true);
    };

    // アップロード完了後の処理
    const handleFileUploaded = (file: File, url: string, id: number) => {
        // アップロードが成功したら uploading を false に設定
        setUploadedFiles((prevUploadedFiles) =>
            prevUploadedFiles.map((uploadedFile) =>
                uploadedFile.file === file ? { ...uploadedFile, id: id, url: url, uploading: false } : uploadedFile
            )
        );
        setRedrawByUpload(new Date());
    };

    // ファイル状態の再描画
    useEffect(() => {
        if (!redrawByDelete) {
            return;
        }

        // 削除されたファイルを除く
        setUploadedFiles((prevUploadedFiles) =>
            prevUploadedFiles.filter((file) => file.id !== redrawByDelete)
        );

        // エラーメッセージ初期化
        setErrorMessage("");
    }, [redrawByDelete]);

    useEffect(() => {
        if (scrollFlag && scrollToMediaCardRef.current) {
            // MediaCardの1枚目が画面下に収まるようにスクロールする
            scrollToMediaCardRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
            setScrollFlag(false);
        }
    }, [scrollFlag, uploadedFiles]);

    return (
        <Box
            sx={{
                maxWidth: 980,
                mx: "auto",
                px: 3,
                py: 3,
            }}
        >
            <Typography
                variant="h5"
                component="h1"
                fontWeight="700"
                gutterBottom
            >ファイルアップロード</Typography>
            <Typography paragraph>このページでは、トピックやコメントに関連する動画等のファイルをアップロードすることができます。アップロード後に発行されるファイル貼り付け用コードをコピー、トピックやコメント内にペーストすれば、ファイルを張り付けることができます。</Typography>

            {/*バリデーションメッセージ*/}
            {errorMessage &&
                <Typography
                    align="center"
                    component="p"
                    variant="caption"
                    sx={{
                        mt: 1,
                        mx: 2,
                        color: (theme) => theme.palette.error.main,
                    }}
                >
                    {errorMessage}
                </Typography>
            }

            {/*ストレージ使用状態*/}
            <Box sx={{ mb: 2 }}>
                <div style={{ width: '40%'}}>
                    <StorageUsageBar
                        used={storageUsed}
                        total={storageTotal}
                    />
                </div>
            </Box>

            {/*メディアの挿入について*/}
            <Box sx={{mb: 2}}>
                <DescriptionInsertMedia />
            </Box>

            <Box
                sx={{
                    mb: 2,
                    px: { mb: 2, tb: 4 },
                    py: { mb: 3, tb: 5 },
                    backgroundColor: (theme) => theme.palette.common.white,
                    borderRadius: 1,
                }}
            >
                <Dropzone
                    storageUsed={storageUsed}
                    storageTotal={storageTotal}
                    setErrorMessage={setErrorMessage}
                    setFileAdditionCount={setScrollIndex}
                    onFileAddition={handleFileAddition}
                    onFileUploaded={handleFileUploaded}
                />
            </Box>

            {/* アップロードされたファイル */}
            <Box >
                <Typography
                    variant="h5"
                    component="h1"
                    fontWeight="700"
                    gutterBottom
                    sx={{mb: 2}}
                >アップロードされたファイル</Typography>

                {uploadedFiles &&
                    uploadedFiles.map((uploadedFile, index) => (
                        <Box
                            ref={
                                index === uploadedFiles.length - scrollIndex ? scrollToMediaCardRef : null
                            }
                            sx={{
                                py: 1
                            }}
                        >
                            <MediaCard
                                id={uploadedFile.id}
                                url={uploadedFile.url}
                                name={uploadedFile.file.name}
                                size={convertBytesToKB(uploadedFile.file.size).toString() + "KB"}
                                uploadedDate={uploadedFile.uploadedDate}
                                uploading={uploadedFile.uploading}
                                handleDeleteMedia={handleDeleteMedia}
                            />
                        </Box>
                    )).reverse()
                }
            </Box>
        </Box>
    );
}